import { ApiErrorMessageWithTrans } from '@components/Error/ApiErrorMessageWithTrans';
import { t } from '@lingui/core/macro';
import { BookingRecordDetailsCustomer } from '@navio/nsdk';
import { URLKeys, noddiAsync } from 'noddi-async';
import { cn } from 'noddi-ui-common';
import { NoddiButton, NoddiCard, NoddiStepFlow, NoddiTextArea } from 'noddi-ui-web';
import { customerRoutes } from 'noddi-util';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FeedbackRatingStep } from './FeedbackRatingStep';
import {
  FEEDBACK_STEP_KEYS,
  FeedbackRatingStepKey,
  FeedbackStepKey,
  getFeedbackQuestions,
  getFeedbackSteps
} from './feedbackSteps';

// Long enough to see the picked star fill before the next question slides in.
const AUTO_ADVANCE_MS = 180;

const NO_RATINGS: Record<FeedbackRatingStepKey, number | null> = {
  EASE_OF_BOOKING: null,
  COMMUNICATION: null,
  RESULT: null,
  POLITENESS: null,
  OVERALL: null
};

export const FeedbackToBooking = ({ booking }: { booking: BookingRecordDetailsCustomer }) => {
  const navigate = useNavigate();
  const [ratings, setRatings] = useState(NO_RATINGS);
  const [comment, setComment] = useState('');
  const [stepKey, setStepKey] = useState<FeedbackStepKey>('EASE_OF_BOOKING');
  // Furthest step shown: every step before it is rated or, when optional, was skipped.
  const [reachedIndex, setReachedIndex] = useState(0);
  // Which way the last step change went, so the next step slides in from that side.
  const [direction, setDirection] = useState<'forward' | 'back'>('forward');
  const autoAdvanceRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => () => clearTimeout(autoAdvanceRef.current), []);

  const {
    mutateAsync: addFeedback,
    isPending: isAddFeedbackPending,
    error: addFeedbackError
  } = noddiAsync.usePost({
    type: URLKeys.postFeedback,
    queryConfig: {
      onSuccess: () => {
        navigate(
          customerRoutes.feedbackSuccess.getPath({
            slug: booking.slug
          })
        );
      }
    }
  });

  const steps = getFeedbackSteps();
  const stepIndex = FEEDBACK_STEP_KEYS.indexOf(stepKey);
  const previousStepKey = FEEDBACK_STEP_KEYS[stepIndex - 1];
  const nextStepKey = FEEDBACK_STEP_KEYS[stepIndex + 1];
  const isOptionalStep = steps[stepIndex]?.optional ?? false;
  const isStepAnswered = stepKey === 'COMMENT' ? comment.trim() !== '' : ratings[stepKey] !== null;
  const areRequiredRated = steps.every(({ key, optional }) => optional || (key !== 'COMMENT' && ratings[key] !== null));
  const completedStepKeys = steps
    .filter(({ key, optional }, index) =>
      key === 'COMMENT' ? false : ratings[key] !== null || (optional && index < reachedIndex)
    )
    .map(({ key }) => key);

  const goToStep = (key: FeedbackStepKey) => {
    clearTimeout(autoAdvanceRef.current);
    const index = FEEDBACK_STEP_KEYS.indexOf(key);
    setDirection(index > stepIndex ? 'forward' : 'back');
    setReachedIndex((reached) => Math.max(reached, index));
    setStepKey(key);
  };

  const rate = (key: FeedbackRatingStepKey, value: number, picked: boolean) => {
    setRatings((current) => ({ ...current, [key]: value }));
    clearTimeout(autoAdvanceRef.current);
    if (picked && nextStepKey) {
      autoAdvanceRef.current = setTimeout(() => goToStep(nextStepKey), AUTO_ADVANCE_MS);
    }
  };

  return (
    <div className='flex flex-col gap-6'>
      <NoddiStepFlow
        steps={steps}
        currentStepKey={stepKey}
        completedStepKeys={completedStepKeys}
        onStepChange={(key) => goToStep(FEEDBACK_STEP_KEYS.find((candidate) => candidate === key) ?? 'EASE_OF_BOOKING')}
      />

      <div
        key={stepKey}
        className={cn(
          'motion-safe:animate-in motion-safe:duration-300 motion-safe:ease-out motion-safe:fade-in',
          direction === 'forward' ? 'motion-safe:slide-in-from-right-8' : 'motion-safe:slide-in-from-left-8'
        )}
      >
        {stepKey === 'COMMENT' ? (
          <NoddiCard contentClassName='flex flex-col gap-4'>
            <NoddiTextArea
              label={t`Other?`}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder={t`Please share your experience with us`}
            />
            {addFeedbackError && <ApiErrorMessageWithTrans error={addFeedbackError} />}
          </NoddiCard>
        ) : (
          <FeedbackRatingStep
            question={getFeedbackQuestions()[stepKey]}
            optional={isOptionalStep}
            rating={ratings[stepKey]}
            onRate={(value, picked) => rate(stepKey, value, picked)}
          />
        )}
      </div>

      <div className='flex justify-between gap-3'>
        <NoddiButton
          variant='outline'
          startIcon='ChevronLeft'
          className='flex-1 md:flex-none'
          disabled={!previousStepKey}
          onClick={() => previousStepKey && goToStep(previousStepKey)}
        >{t`Back`}</NoddiButton>
        {nextStepKey ? (
          <NoddiButton
            endIcon='ChevronRight'
            className='flex-1 md:flex-none'
            disabled={!isOptionalStep && !isStepAnswered}
            onClick={() => goToStep(nextStepKey)}
          >
            {isOptionalStep && !isStepAnswered ? t`Skip` : t`Continue`}
          </NoddiButton>
        ) : (
          <NoddiButton
            loading={isAddFeedbackPending}
            className='flex-1 md:flex-none'
            disabled={!areRequiredRated}
            onClick={() => {
              // oxlint-disable-next-line typescript/no-floating-promises -- mutateAsync result unused; the comment step renders addFeedbackError from the same mutation
              addFeedback({
                customerComment: comment,
                customerRatingCarResult: ratings.RESULT!,
                customerRatingCommunication: ratings.COMMUNICATION!,
                customerRatingOverall: ratings.OVERALL!,
                customerRatingPoliteness: ratings.POLITENESS,
                customerRatingEaseOfUse: ratings.EASE_OF_BOOKING!,
                bookingId: booking.id
              });
            }}
          >{t`Send feedback`}</NoddiButton>
        )}
      </div>
    </div>
  );
};
